import { Clock, type LucideIcon } from "lucide-react";
import type { SiteLang } from "@/hooks/useLanguage";
import type { OfferingSection } from "@/components/services/GenericOfferingContent";

/* ─────────────────────────────────────────────────────
   Spanish translations for QAOffering page metadata
   ───────────────────────────────────────────────────── */

interface OfferingPageMeta {
  title: string;
  subtitle: string;
  description: string;
  faqItems?: { q: string; a: string }[];
}

const offeringMetaES: Record<string, OfferingPageMeta> = {
  "qms-setup": {
    title: "Implementación de sistemas de gestión de calidad",
    subtitle: "Garantiza el cumplimiento y la eficiencia con sistemas de gestión de calidad a medida",
    description:
      "En QbD Group ponemos a tu disposición expertos en QA con amplia experiencia en la creación de soluciones QMS inteligentes, eficaces y conformes, adaptadas a tus necesidades. Ya sea que tu enfoque sea GMP, GCP, GDP o estándares ISO, nos aseguramos de que tu sistema aporte valor al negocio y cumpla la normativa.",
  },
  "qms-remediation-support": {
    title: "Actualización y mantenimiento de sistemas de gestión de la calidad",
    subtitle: "Mantén tu sistema de gestión de calidad conforme y eficaz",
    description:
      "QbD Group ofrece expertos en QA con experiencia en la gestión y actualización de sistemas de gestión de calidad para garantizar el cumplimiento continuo y la excelencia operativa. Desde la remediación de brechas hasta el soporte diario del QMS, adaptamos nuestros servicios a tus necesidades.",
  },
  "outsourced-qa": {
    title: "Externalización del control de calidad",
    subtitle: "Simplifica la gestión de la calidad con servicios de QA externalizados a medida",
    description:
      "En QbD Group nos encargamos de tus actividades de Quality Assurance con un equipo dedicado de expertos. Desde la gestión de proveedores hasta la gestión de desviaciones, ofrecemos soluciones eficientes y rentables para que puedas centrarte en tu estrategia principal.",
    faqItems: [
      { q: "¿Por qué deberíamos externalizar nuestro sistema de gestión de calidad?", a: "La externalización te permite acceder a experiencia especializada, asegurando el cumplimiento normativo sin la carga de gestionarlo internamente. Te permite centrarte en tu negocio principal mientras los expertos se encargan de las complejidades de la gestión de la calidad." },
      { q: "¿Cómo puede la externalización mejorar el cumplimiento?", a: "La externalización garantiza que tu sistema de calidad sea gestionado por profesionales actualizados con las normativas en constante evolución. Reduce el riesgo de errores de cumplimiento y asegura que tus procesos estén siempre listos para auditoría." },
      { q: "¿Cómo sabemos que nosotros y, por extensión, vuestros consultores sois fiables?", a: "Formamos y mantenemos cuidadosamente la experiencia, certificaciones y trayectoria de nuestros consultores. Tendrás transparencia durante todo el proceso, con actualizaciones periódicas y revisiones de rendimiento." },
      { q: "¿Puede la externalización ayudar a reducir costes?", a: "Sí, la externalización elimina la necesidad de contrataciones a largo plazo y costes generales, ofreciendo una solución más rentable. Te permite pagar por los servicios según los necesites, sin sacrificar la calidad." },
      { q: "¿Qué tipos de servicios de gestión de calidad se pueden externalizar?", a: "Puedes externalizar una amplia gama de servicios, incluyendo auditorías, formación, presentaciones regulatorias, gestión documental, revisión de lotes, gestión de proveedores y más. Esto te permite adaptar la solución de externalización a tus necesidades." },
      { q: "¿Con qué rapidez se puede implementar la externalización?", a: "El plazo depende de tus necesidades, pero trabajamos de forma eficiente para integrar la externalización con mínima disrupción. Normalmente, podemos tener todo en marcha en un mes." },
      { q: "¿Perderemos el control sobre nuestros procesos de calidad?", a: "¡En absoluto! Mantendrás el control, con flexibilidad para establecer expectativas y supervisar el progreso. La externalización complementa tus operaciones, proporcionando soporte experto sin perder la supervisión." },
    ],
  },
  "qualified-person-qp-responsible-person-rp": {
    title: "Persona Cualificada (PC) y Persona Responsable (PR)",
    subtitle: "Cumple los requisitos regulatorios de forma fluida con soporte experto de QP y RP",
    description:
      "QbD Group proporciona Personas Cualificadas y Personas Responsables con experiencia para ayudarte a cumplir los requisitos obligatorios, mantener el cumplimiento y asegurar la continuidad operativa. Desde la liberación de lotes hasta la supervisión del cumplimiento, nuestros expertos atienden tus necesidades.",
    faqItems: [
      { q: "¿Qué cualificaciones debe tener un QP o RP?", a: "Una Persona Cualificada (QP) o Persona Responsable (RP) debe poseer titulaciones relevantes en Life Sciences y certificaciones que cumplan los estándares regulatorios como GMP o GDP. Deben tener amplia experiencia y conocimiento de las regulaciones de la UE o la FDA." },
      { q: "¿Con qué rapidez se puede contratar un QP o RP a través de QbD?", a: "Dependiendo de tus necesidades, un QP o RP cualificado puede estar normalmente disponible en pocas semanas." },
      { q: "¿Puede un QP o RP trabajar en remoto?", a: "Sí, muchas tareas como la documentación de cumplimiento y la supervisión pueden realizarse en remoto. Sin embargo, ciertas responsabilidades, como la liberación de lotes, pueden requerir presencia in situ." },
      { q: "¿Cómo os aseguráis de que el QP o RP cumpla con las regulaciones?", a: "Nuestros QPs y RPs se mantienen actualizados a través de cursos y certificaciones de la industria. La retroalimentación periódica y las revisiones de rendimiento garantizan altos estándares de cumplimiento." },
      { q: "¿Y si necesitamos un QP o RP para un proyecto a corto plazo?", a: "Ofrecemos compromisos flexibles a corto plazo para auditorías, lanzamientos de productos o presentaciones regulatorias, proporcionando soporte sin compromiso a largo plazo." },
      { q: "¿Perderemos el control sobre nuestros procesos de cumplimiento?", a: "En absoluto. Nuestros QPs y RPs se integran de forma fluida en tu equipo, siguiendo tus protocolos y procedimientos mientras proporcionan orientación experta. Mantienes la supervisión completa y la autoridad en la toma de decisiones." },
    ],
  },
  "annual-product-review-apr-product-quality-review-pqr": {
    title: "Revisión Anual de la Calidad del Producto (APQR / PQR)",
    subtitle: "Optimiza tus procesos de APR y PQR con soporte experto",
    description:
      "QbD Group ofrece soporte integral para la Revisión Anual de Producto y la Revisión de Calidad de Producto — desde el análisis de datos y la redacción del PQR hasta la revisión de cumplimiento. Estas revisiones obligatorias aseguran la calidad, seguridad y preparación para el mercado.",
    faqItems: [
      { q: "¿Qué información necesitáis de nosotros para iniciar el APR?", a: "Necesitaremos acceso a los datos relevantes del producto, registros de lotes, métricas de calidad y documentos de revisiones anteriores. Te guiaremos durante el proceso de recopilación de datos para asegurar que todo esté en orden." },
      { q: "¿Podéis gestionar productos complejos con múltiples componentes?", a: "Sí, tenemos experiencia con productos complejos y sistemas multicomponente. Nuestro equipo asegura que cada componente sea evaluado correctamente." },
      { q: "¿Cómo gestionáis la información confidencial del producto?", a: "Seguimos protocolos estrictos para gestionar tus datos de producto de forma segura. Toda la información se comparte únicamente dentro de nuestro equipo de confianza." },
      { q: "¿Y si tenemos múltiples productos que necesitan APRs?", a: "Podemos gestionar APRs de múltiples productos simultáneamente, asegurando consistencia y optimizando el proceso." },
      { q: "¿Cómo abordáis los problemas o desviaciones encontrados durante el APR?", a: "Identificamos las desviaciones durante la revisión y proporcionamos recomendaciones claras para acciones correctivas, con un plan de acción detallado." },
      { q: "¿Podéis integrar nuestro sistema de gestión de calidad existente en el proceso de APR?", a: "Sí, nos alineamos con tu QMS existente para asegurar que el proceso de revisión se integre de forma fluida con tus procedimientos internos." },
      { q: "¿Cómo sabremos que el APR se completará a tiempo?", a: "Proporcionamos un calendario claro y actualizaciones periódicas, para que siempre sepas cuándo esperar la revisión completada." },
    ],
  },
  "audits-and-remediation": {
    title: "Auditoría de calidad",
    subtitle: "Garantiza el cumplimiento y la mejora continua con servicios de auditoría expertos",
    description:
      "QbD Group ofrece auditores certificados y experimentados para realizar auditorías internas y de proveedores en tu nombre. Nuestros expertos en QA también pueden ayudarte a resolver hallazgos de auditoría de forma eficaz, asegurando el cumplimiento e impulsando la mejora.",
  },
};

const offeringMetaZH: Record<string, OfferingPageMeta> = {
  "qms-setup": {
    title: "质量管理体系搭建",
    subtitle: "通过量身定制的质量管理体系,确保合规与高效运营",
    description:
      "QbD Group 拥有经验丰富的 QA 专家,擅长为您打造智能、高效且合规的 QMS 解决方案。无论您聚焦于 GMP、GCP、GDP 还是 ISO 标准,我们都将确保您的体系既能为业务赋能,又能满足法规要求。",
  },
  "qms-remediation-support": {
    title: "QMS 整改与支持",
    subtitle: "确保您的质量管理体系持续合规并高效运行",
    description:
      "QbD Group 提供经验丰富的 QA 专家,帮助您管理和升级质量管理体系,确保持续合规与卓越运营。从差距整改到日常 QMS 支持,我们根据您的需求量身定制服务。",
  },
  "qualified-person-qp-responsible-person-rp": {
    title: "合格人员 (QP) 与负责人 (RP) 服务",
    subtitle: "借助专业的 QP 与 RP 支持,无缝满足法规要求",
    description:
      "QbD Group 提供经验丰富的合格人员 (QP) 与负责人 (RP),帮助您满足强制性要求、保持合规并确保运营连续性。从批次放行到合规监控,我们的专家全方位支持您的需求。",
    faqItems: [
      { q: "QP 或 RP 应当具备哪些资质?", a: "合格人员 (QP) 或负责人 (RP) 应当持有生命科学领域的相关学位,以及符合 GMP 或 GDP 等法规标准的认证。他们必须具备丰富的经验,并熟悉欧盟或 FDA 的法规要求。" },
      { q: "通过 QbD Group 招募 QP 或 RP 需要多长时间?", a: "根据您的需求,通常可以在几周内提供合格的 QP 或 RP。" },
      { q: "QP 或 RP 可以远程工作吗?", a: "可以,合规文件管理与监督等许多工作都可以远程完成。但某些职责(例如批次放行)可能需要现场履行。" },
      { q: "你们如何确保 QP 或 RP 持续符合法规要求?", a: "我们的 QP 与 RP 通过行业课程和认证持续更新知识。定期的反馈与绩效评估确保他们始终保持高水平的合规表现。" },
      { q: "如果我们只需要短期项目的 QP 或 RP 怎么办?", a: "我们为审计、产品上市或法规申报等场景提供灵活的短期合作,提供专业支持而无需长期承诺。" },
      { q: "我们会失去对合规流程的控制权吗?", a: "完全不会。我们的 QP 与 RP 会无缝融入您的团队,遵循您的协议与流程,同时提供专业指导。您始终保留完整的监督权与决策权。" },
    ],
  },
  "audits-and-remediation": {
    title: "QA 审计与整改",
    subtitle: "借助专业的审计服务,确保合规并实现持续改进",
    description:
      "QbD Group 提供经认证且经验丰富的审计师,代表您执行内部审计与供应商审计。我们的 QA 专家还可协助您有效解决审计发现,确保合规并推动持续改进。",
  },
  "outsourced-qa": {
    title: "QA 外包",
    subtitle: "通过量身定制的 QA 外包服务,简化质量管理",
    description:
      "QbD Group 由专属专家团队接管您的质量保证活动。从供应商管理到偏差处理,我们提供无缝、高性价比的解决方案,让您专注于核心战略。",
    faqItems: [
      { q: "为什么要将质量管理体系外包?", a: "外包让您获得专业领域的深厚经验,在无需承担内部管理负担的情况下确保合规。您可以专注于核心业务,而由专家处理质量管理的复杂工作。" },
      { q: "外包如何提升合规水平?", a: "外包确保您的质量体系由始终掌握最新法规的专业人士管理。这能降低合规失误的风险,并确保您的流程随时具备接受审计的能力。" },
      { q: "我们如何确信你们以及你们的顾问值得信赖?", a: "我们对顾问的经验、认证与履历进行严格的培训与维护。在整个合作过程中,您将享有全程透明,并获得定期更新与绩效评估。" },
      { q: "外包能否帮助降低成本?", a: "可以。外包免除了长期招聘与固定开支的需要,提供更具成本效益的方案。您可以按需付费,同时不牺牲质量。" },
      { q: "可以外包哪些类型的质量管理服务?", a: "您可以外包多种服务,包括审计、培训、法规申报、文件管理、批次审查、供应商管理等等。您可以根据自身需求定制外包方案。" },
      { q: "外包的实施速度有多快?", a: "时间取决于您的需求,但我们高效推进,将外包整合到您的运营中,把干扰降到最低。通常我们可在一个月内顺利运转。" },
      { q: "我们会失去对质量流程的控制权吗?", a: "完全不会!您依然掌握主导权,可以灵活设定预期并监督进度。外包是对您运营的补充,在不丧失监督权的前提下提供专业支持。" },
    ],
  },
};


export const getQAOfferingMeta = (
  contentKey: string,
  lang: SiteLang,
): OfferingPageMeta | undefined => {
  if (lang === "es") return offeringMetaES[contentKey];
  if (lang === "zh-cn") return offeringMetaZH[contentKey];
  return undefined;
};

/* ─────────────────────────────────────────────────────
   Full section-level Spanish content overrides
   (text only — icons are preserved from the English base)
   ───────────────────────────────────────────────────── */

type OfferingSectionTextOverride = {
  whatTitle: string;
  whatParagraphs: string[];
  whatImageAlt?: string;
  whyTitle: string;
  whyIntro?: string;
  whyBenefits: { title: string; text: string; icon?: LucideIcon }[];
  helpCards?: {
    heading: string;
    intro: string;
    cards: { title: string; text: string }[];
  };
  extraSections?: { title: string; paragraphs: string[] }[];
  processTitle?: string;
  processIntro?: string;
  processSteps?: { title: string; text: string }[];
  processImageAlt?: string;
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
};

const offeringContentES: Record<string, OfferingSectionTextOverride> = {
  /* ───────── QMS Setup ───────── */
  "qms-setup": {
    whatTitle: "¿Qué es un QMS?",
    whatParagraphs: [
      "Un sistema de gestión de calidad (QMS) es un marco estructurado de procesos y procedimientos diseñado para garantizar la calidad, seguridad y eficacia de los productos en el sector de Life Sciences. En QbD Group apoyamos a las organizaciones en la implementación de soluciones QMS que se alineen con su tamaño, alcance y complejidad.",
      "Los aspectos clave de la implementación de un QMS incluyen: <strong class='text-foreground'>Cumplimiento normativo</strong> (ISO 13485:2016, 21 CFR 820, Reglamentos UE 2017/745 y 746, MDSAP), <strong class='text-foreground'>soluciones a medida</strong> adaptadas a las actividades específicas de tu organización, <strong class='text-foreground'>documentación</strong> con desarrollo de todos los procedimientos, políticas y manuales necesarios, <strong class='text-foreground'>formación de stakeholders</strong> para adoptar y mantener los estándares QMS de forma eficaz, y <strong class='text-foreground'>mejora continua</strong> integrando prácticas que impulsen la eficiencia y reduzcan el desperdicio.",
    ],
    whyTitle: "¿Por qué es importante un QMS?",
    whyIntro: "La implementación de un QMS no solo es obligatoria para el cumplimiento normativo, sino que también aporta beneficios significativos al negocio:",
    whyBenefits: [
      { title: "Obligatorio para la certificación", text: "Sin QMS, no hay certificación de producto ni acceso al mercado." },
      { title: "Consistencia operativa", text: "Los procesos estandarizados conducen a menos errores e ineficiencias." },
      { title: "Mejora continua", text: "Un QMS fomenta un entorno de optimización permanente." },
      { title: "Mejor toma de decisiones", text: "Los procesos basados en evidencia permiten mejores decisiones de negocio." },
      { title: "Satisfacción del cliente", text: "Una mejor calidad de producto se traduce en mayor confianza del paciente y del cliente." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "QbD Group ofrece servicios de implementación de QMS flexibles e integrales para cubrir tus necesidades únicas:",
      cards: [
        { title: "Implementación completa de QMS", text: "Workshops para definir requisitos, creación de toda la documentación necesaria y formación de stakeholders." },
        { title: "Coaching y soporte", text: "Workshops de introducción a los requisitos, revisión y perfeccionamiento de la documentación de tu organización." },
        { title: "Asistencia para startups", text: "Workshops introductorios para guiar a tu equipo en la comprensión y planificación de la implementación del QMS." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios de implementación de QMS flexibles e integrales. Desde la implementación completa hasta el coaching y la asistencia inicial, adaptamos nuestro enfoque a tus necesidades.",
    processSteps: [
      { title: "1. Evaluación", text: "Analizamos tus procesos actuales e identificamos brechas." },
      { title: "2. Workshops", text: "Trabajamos con tus stakeholders para comprender los requisitos y diseñar soluciones." },
      { title: "3. Desarrollo de documentación", text: "Creamos procedimientos, políticas y manuales a medida que cumplen los estándares regulatorios." },
      { title: "4. Formación e implementación", text: "Dotamos a tu equipo de las competencias para adoptar, implementar y mantener el QMS." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Cuando te asocias con QbD Group para la implementación de tu QMS, te beneficias de:",
    partnerBullets: [
      "Experiencia demostrada — décadas de trayectoria en Quality Assurance y cumplimiento normativo.",
      "Enfoques a medida — soluciones adaptadas al tamaño, complejidad y objetivos de tu organización.",
      "Cumplimiento por diseño — procedimientos creados para integrarse de forma fluida con tus flujos de trabajo actuales.",
      "Conocimiento multidisciplinar — experiencia en QA, RA, validación y más.",
      "Soluciones escalables — servicios diseñados para crecer con tu organización.",
    ],
  },

  /* ───────── QMS Remediation & Support ───────── */
  "qms-remediation-support": {
    whatTitle: "¿Qué es la remediación y soporte de QMS?",
    whatParagraphs: [
      "Implementar un QMS es solo el primer paso; mantenerlo y mejorarlo es un proceso continuo. Los servicios de remediación y soporte de QMS de QbD Group proporcionan orientación experta para mantener tu QMS operativo, conforme y en mejora continua.",
      "Ya sea que necesites remediación de brechas identificadas en auditorías o inspecciones, o que requieras soporte para las actividades diarias del QMS, adaptamos nuestros servicios a las necesidades específicas de tu organización. Los aspectos clave incluyen <strong class='text-foreground'>soporte operativo</strong>, <strong class='text-foreground'>remediación de brechas</strong>, <strong class='text-foreground'>mejora continua</strong>, <strong class='text-foreground'>gestión documental</strong> y <strong class='text-foreground'>aseguramiento del cumplimiento</strong> — ayudándote a alcanzar y mantener la certificación.",
    ],
    whyTitle: "¿Por qué es importante la remediación y soporte del QMS?",
    whyIntro: "Mantener un QMS eficaz es esencial para el cumplimiento normativo y el éxito operativo:",
    whyBenefits: [
      { title: "Actualizaciones normativas", text: "Las regulaciones evolucionan y tu QMS debe adaptarse para seguir siendo conforme." },
      { title: "Preparación para inspecciones", text: "La documentación y los procesos adecuados aseguran que tu QMS resista auditorías e inspecciones." },
      { title: "Consistencia operativa", text: "Los procesos estandarizados reducen errores y garantizan operaciones fluidas." },
      { title: "Mejora continua", text: "La supervisión y el análisis continuos ayudan a identificar brechas de forma temprana y abordarlas proactivamente." },
      { title: "Mitigación de riesgos", text: "La detección y resolución temprana de problemas previene costosas sanciones por incumplimiento." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "QbD Group ofrece servicios flexibles de remediación y soporte de QMS adaptados a las necesidades únicas de tu organización:",
      cards: [
        { title: "Soporte de remediación", text: "Aborda brechas identificadas en inspecciones, como 483s, Warning Letters o hallazgos de auditoría. Implementa acciones correctivas y preventivas (CAPA) para asegurar el cumplimiento." },
        { title: "Soporte operativo del QMS", text: "Gestión diaria de actividades del QMS, incluyendo evaluaciones de riesgos, documentación, supervisión continua, análisis y verificaciones de eficacia." },
        { title: "Experiencia a medida", text: "Profesionales de QA experimentados para apoyar a tu equipo in situ o en remoto, con soluciones escalables desde nivel junior hasta senior." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios flexibles de remediación y soporte de QMS adaptados a las necesidades únicas de tu organización, desde soporte de remediación hasta gestión operativa del QMS y experiencia a medida.",
    processSteps: [
      { title: "1. Evaluación", text: "Evaluamos tu QMS e identificamos brechas o áreas de mejora." },
      { title: "2. Plan de acción personalizado", text: "Desarrollamos un plan adaptado a tus necesidades y requisitos regulatorios." },
      { title: "3. Implementación", text: "Nuestros expertos asisten con la remediación, el soporte operativo y la documentación." },
      { title: "4. Supervisión continua", text: "Nos aseguramos de que tu QMS siga siendo conforme y eficaz mediante actualizaciones y evaluaciones periódicas." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Cuando te asocias con QbD Group para la remediación y soporte de tu QMS, te beneficias de:",
    partnerBullets: [
      "Amplia experiencia — más de 150 expertos con trayectoria en organizaciones pequeñas y grandes, diversos tipos de productos y múltiples idiomas.",
      "Conocimiento regulatorio — numerosas interacciones con autoridades competentes aseguran el cumplimiento en todos los niveles.",
      "Soluciones a medida — soporte flexible diseñado para afrontar los retos únicos de tu organización.",
      "Éxito demostrado — trayectoria de garantía de cumplimiento y excelencia operativa para clientes globales.",
      "Alcance global — soporte presencial y remoto disponible según tus necesidades.",
    ],
  },

  /* ───────── Outsourced QA ───────── */
  "outsourced-qa": {
    whatTitle: "¿Qué es la externalización de QA?",
    whatParagraphs: [
      "La externalización de QA implica delegar las actividades de tu sistema de gestión de calidad (QMS) a un equipo de profesionales con experiencia en la normativa de Life Sciences. Ya necesites soporte para todo el sistema o funciones de calidad específicas, ofrecemos soluciones escalables y flexibles para cubrir las necesidades de tu organización.",
      "Los aspectos clave incluyen: <strong class='text-foreground'>procesos estandarizados</strong> entre equipos, plantas y proveedores, <strong class='text-foreground'>modelos de recursos flexibles</strong> que solucionan los cuellos de botella en la contratación, <strong class='text-foreground'>profunda experiencia regulatoria</strong> para cubrir las brechas de conocimiento, <strong class='text-foreground'>escalar el soporte de QA de forma fluida</strong> para adaptarse a las necesidades cambiantes del negocio y gestionar cargas de trabajo fluctuantes, <strong class='text-foreground'>gestión proactiva del riesgo de cumplimiento</strong> para evitar retrasos y disrupciones costosas, e <strong class='text-foreground'>integración eficiente con sistemas validados</strong> para mantener los estándares de calidad con costes controlados.",
    ],
    whyTitle: "¿Cómo podemos ayudar?",
    whyBenefits: [
      { title: "Acceso a experiencia especializada", text: "Profunda experiencia en áreas nicho que asegura el cumplimiento preciso de la normativa, reduciendo riesgos y acelerando la resolución de problemas." },
      { title: "Despliegue optimizado de recursos", text: "Fuerza laboral remota escalable que permite a las empresas ampliar o reducir rápidamente según las necesidades del proyecto." },
      { title: "Más rapidez en la comercialización", text: "Céntrate en actividades críticas de I+D mientras nosotros nos encargamos de las tareas no esenciales, minimizando cuellos de botella y acelerando el desarrollo de productos." },
      { icon: Clock, title: "Operaciones 24/7", text: "Con servicios externalizados en diferentes zonas horarias, el trabajo continúa las 24 horas, maximizando la productividad." },
    ],
    extraSections: [
      {
        title: "Qué ofrecemos: experiencia en Quality Assurance escalable y a medida",
        paragraphs: [
          "Soporte a medida para tus actividades de QA con soporte base de negocio y una fuerza laboral remota escalable (1–25 especialistas).",
          "Nuestro catálogo de servicios incluye: QA, QP, RP, auditores, auditores líderes, CAPA, cambios, desviaciones, technical writers y service managers.",
          "Aportamos amplia experiencia sectorial en <strong class='text-foreground'>GMP, GDP, GCP, MD, IVD y ATMP</strong>, con expertise regional y conocimiento local de las autoridades competentes (EU GMP, US GMP, ANVISA, Health Canada).",
        ],
      },
    ],
    processTitle: "¿Por qué QbD Group?",
    processIntro: "Cuando eliges QbD Group para la externalización de QA, obtienes profunda experiencia funcional respaldada por service managers experimentados que aportan orientación pragmática y de alto nivel.",
    processSteps: [
      { title: "Modelo escalable probado", text: "Obtiene talento especializado del pool de expertos de QbD Group a medida que evolucionan tus necesidades." },
      { title: "Soluciones de QA personalizadas", text: "Diseñadas para alinearse con tus objetivos, respaldadas por métricas claras e indicadores de rendimiento." },
      { title: "Opciones de entrega global", text: "Capacidades multilingües y modelos de colaboración flexibles e híbridos." },
      { title: "Claridad presupuestaria", text: "Total transparencia y previsibilidad desde el inicio, asegurando alineación con las expectativas financieras." },
    ],
    partnerTitle: "¿Por qué asociarse con QbD Group?",
    partnerIntro: "QbD Group proporciona soluciones flexibles de QA externalizado adaptadas a tus necesidades:",
    partnerBullets: [
      "Profunda experiencia funcional respaldada por service managers experimentados que aportan orientación pragmática y de alto nivel.",
      "Éxito probado con un modelo de soporte escalable que obtiene talento especializado del pool de expertos de QbD Group a medida que evolucionan tus necesidades.",
      "Soluciones de QA personalizadas diseñadas para alinearse con tus objetivos, respaldadas por métricas claras e indicadores de rendimiento.",
      "Opciones de entrega global con capacidades multilingües y modelos de colaboración flexibles e híbridos.",
      "Comunicación transparente y estructurada, incluyendo informes de progreso consistentes y mecanismos de escalado.",
      "Claridad y previsibilidad presupuestaria desde el inicio, asegurando alineación con las expectativas financieras y el alcance operativo.",
    ],
  },

  /* ───────── QP/RP Services ───────── */
  "qualified-person-qp-responsible-person-rp": {
    whatTitle: "¿Qué es el soporte QP/RP?",
    whatParagraphs: [
      "Los roles de Persona Cualificada (QP) y Persona Responsable (RP) son obligatorios para garantizar la calidad y el cumplimiento de los productos farmacéuticos y la distribución. Estos profesionales supervisan el cumplimiento de los requisitos regulatorios, gestionan la liberación de productos y aseguran la adherencia a las autorizaciones de comercialización.",
      "Los aspectos clave incluyen: <strong class='text-foreground'>cumplimiento normativo</strong> con estándares GMP o GDP, <strong class='text-foreground'>supervisión de liberación de lotes</strong>, <strong class='text-foreground'>documentación y trazabilidad</strong>, <strong class='text-foreground'>soporte flexible</strong> con opciones de dedicación completa, interina, por proyecto o a tiempo parcial, y <strong class='text-foreground'>experiencia especializada</strong> con conocimiento avanzado en regulaciones y prácticas de Life Sciences.",
    ],
    whyTitle: "¿Por qué es importante el soporte QP/RP?",
    whyIntro: "Disponer de un QP o RP es obligatorio para empresas farmacéuticas, importadores y distribuidores. Estos roles son clave para asegurar:",
    whyBenefits: [
      { title: "Liberación de producto", text: "El cumplimiento de los estándares regulatorios es necesario para que los productos accedan al mercado." },
      { title: "Preparación para auditorías", text: "La documentación adecuada y la adherencia a las regulaciones mitigan los riesgos durante las inspecciones." },
      { title: "Continuidad operativa", text: "Profesionales cualificados cubren roles esenciales, evitando disrupciones." },
      { title: "Experiencia regulatoria", text: "Profesionales con conocimiento aseguran el cumplimiento de regulaciones en evolución." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "QbD Group proporciona servicios de QP y RP a medida para cubrir tus necesidades regulatorias y operativas:",
      cards: [
        { title: "Soporte a tiempo completo", text: "QP o RP dedicado para gestionar las operaciones continuas y el cumplimiento." },
        { title: "Soporte interino", text: "Cobertura temporal durante auditorías, ausencias o picos de carga de trabajo." },
        { title: "Asignaciones por proyecto", text: "Soporte para proyectos específicos como lanzamientos de productos o preparación para inspecciones." },
        { title: "Dedicación a tiempo parcial", text: "Supervisión flexible y revisión de lotes sin compromiso a tiempo completo." },
        { title: "Experiencia bajo demanda", text: "Acceso a QPs o RPs para tareas urgentes o retos regulatorios." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group proporciona servicios de QP y RP a medida para cubrir tus necesidades regulatorias y operativas, desde soporte a tiempo completo hasta experiencia bajo demanda.",
    processSteps: [
      { title: "Consulta inicial", text: "Comentamos tus necesidades específicas y requisitos regulatorios." },
      { title: "Selección de candidatos", text: "Presentamos candidatos QP/RP cualificados según su experiencia y conocimiento de cumplimiento." },
      { title: "Onboarding", text: "El QP o RP se integra en tus operaciones, asegurando una transición fluida." },
      { title: "Soporte continuo", text: "Compromiso continuo para cumplir tus objetivos de cumplimiento y operativos." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Cuando eliges QbD Group para servicios de QP/RP, te beneficias de:",
    partnerBullets: [
      "Disponibilidad inmediata — cubre roles críticos rápidamente sin retrasos.",
      "Experiencia líder en la industria — accede a profesionales con conocimiento avanzado en GMP y GDP.",
      "Soluciones a medida — compromisos flexibles diseñados para cubrir tus necesidades específicas.",
      "Trayectoria probada — confianza de organizaciones de Life Sciences en todo el mundo.",
      "Soporte escalable — amplía o reduce el soporte para ajustarse a tu carga de trabajo y necesidades regulatorias.",
    ],
  },

  /* ───────── APR/PQR ───────── */
  "annual-product-review-apr-product-quality-review-pqr": {
    whatTitle: "¿Qué es el soporte APR/PQR?",
    whatParagraphs: [
      "Un APR o PQR evalúa la calidad y el rendimiento de los productos para asegurar la seguridad, el cumplimiento y la preparación para el mercado. QbD Group proporciona soporte integral para todo el proceso, desde el análisis de datos hasta la elaboración de informes completos.",
      "Los aspectos clave incluyen: <strong class='text-foreground'>gestión integral de datos</strong> — recopilación, procesamiento y análisis de datos de múltiples sistemas, <strong class='text-foreground'>cumplimiento normativo</strong> — asegurando que todas las revisiones cumplan los estándares GMP, <strong class='text-foreground'>informes detallados</strong> con planes de acción propuestos, e <strong class='text-foreground'>insights anuales</strong> proporcionando análisis de tendencias entre PQRs.",
    ],
    whyTitle: "¿Por qué son importantes los APR/PQR?",
    whyBenefits: [
      { title: "Seguridad del paciente", text: "Los procesos de APR y PQR detectan riesgos potenciales, asegurando que los productos cumplan los estándares de seguridad y protegiendo la salud del paciente." },
      { title: "Estabilidad de la cadena de suministro", text: "Las revisiones oportunas previenen contratiempos en la producción y desabastecimientos, asegurando un flujo de producto consistente." },
      { title: "Cumplimiento normativo", text: "Los APR/PQR son un requisito GMP y se revisan durante las inspecciones. Unas revisiones completas y puntuales demuestran que tus productos se mantienen en estado de control." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "QbD Group ofrece servicios de APR/PQR a medida para cubrir las necesidades únicas de tu organización:",
      cards: [
        { title: "Gestión de plazos", text: "Asegurando la finalización oportuna de todas las revisiones." },
        { title: "Redacción de PQR", text: "Elaboración de informes integrales listos para aprobación." },
        { title: "Análisis de datos y estadísticas", text: "Procesamiento de cifras e identificación de tendencias." },
        { title: "Revisión de cumplimiento", text: "Asegurando consistencia y alineación regulatoria." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Desde la recopilación de datos hasta las acciones de seguimiento, cubrimos todo el ciclo APR/PQR, ya necesites establecer, mejorar o ejecutar tu proceso.",
    processSteps: [
      { title: "Recopilación de datos", text: "Reunimos los datos de producción, desviaciones, calidad y tendencias de tus procesos y sistemas, incluidos los datos de tus CDMO cuando la fabricación está externalizada." },
      { title: "Análisis de tendencias", text: "Analizamos los datos e identificamos las tendencias que requieren atención." },
      { title: "Redacción del informe", text: "Redactamos un informe APR/PQR completo, listo para tu revisión." },
      { title: "Revisión de calidad y seguimiento", text: "Revisamos el informe en busca de cumplimiento y coherencia, e identificamos las acciones de seguimiento." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Asociarse con QbD Group para soporte de APR/PQR proporciona:",
    partnerBullets: [
      "Conocimiento experto — amplia experiencia que asegura una evaluación de datos precisa y exhaustiva.",
      "Informes listos para aprobación — recibe informes con insights accionables, listos para tu aprobación.",
      "Gestión de la carga de trabajo — reduce la carga sobre tu equipo de QA durante períodos pico.",
      "Soluciones escalables — gestiona múltiples productos o revisiones de forma eficiente y consistente.",
    ],
  },

  /* ───────── Audits & Remediation ───────── */
  "audits-and-remediation": {
    whatTitle: "¿Qué son las auditorías de QA y la remediación?",
    whatParagraphs: [
      "Las auditorías de QA son un componente crítico de cualquier sistema de gestión de calidad (QMS). Las auditorías internas y las autoinspecciones aseguran el rendimiento y el cumplimiento de tu organización, mientras que las auditorías a proveedores son esenciales para la cualificación y el seguimiento.",
      "QbD Group proporciona servicios de auditoría adaptados a tus necesidades específicas, entregando informes detallados e insights accionables. Aspectos clave de nuestros servicios de auditoría:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong class='text-foreground'>Auditorías internas:</strong> Evaluaciones exhaustivas para asegurar que tu QMS cumple los estándares regulatorios.</li><li><strong class='text-foreground'>Auditorías a proveedores:</strong> Cualificación y seguimiento de proveedores para asegurar cumplimiento y fiabilidad.</li><li><strong class='text-foreground'>Programas personalizados:</strong> Gestión de todo tu plan de auditoría, incluyendo informes individuales y resúmenes globales.</li><li><strong class='text-foreground'>Informes detallados:</strong> Hallazgos claros, no conformidades y recomendaciones para acciones correctivas y preventivas.</li><li><strong class='text-foreground'>Soporte de seguimiento:</strong> Revisión opcional de planes de acción para abordar los hallazgos de auditoría.</li></ul>",
    ],
    whyTitle: "¿Por qué son importantes las auditorías de QA?",
    whyIntro: "Las auditorías no son solo un requisito regulatorio; son oportunidades de crecimiento y mejora:",
    whyBenefits: [
      { title: "Asegurar el cumplimiento", text: "Las auditorías periódicas se alinean con las expectativas de las autoridades y las regulaciones en evolución." },
      { title: "Mejorar los procesos", text: "Identifica ineficiencias y permite actuar, incluso en equipos dispersos." },
      { title: "Anticipar y gestionar riesgos", text: "Detecta problemas tempranos como cambios en proveedores para evitar disrupciones." },
      { title: "Mejorar la comunicación en planta", text: "Refuerza la implicación con los equipos locales para una ejecución más fluida." },
      { title: "Demostrar compromiso bajo presión", text: "Mantén la calidad y el cumplimiento a pesar de los retos operativos." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudar?",
      intro: "QbD Group ofrece servicios de auditoría flexibles y escalables para cubrir las necesidades de tu organización:",
      cards: [
        { title: "Experiencia especializada", text: "Benefíciate de un profundo conocimiento regulatorio en áreas nicho para asegurar el cumplimiento y reducir riesgos." },
        { title: "Recursos flexibles", text: "Escala equipos rápidamente para ajustarse a las necesidades del proyecto y evitar retrasos costosos." },
        { title: "Imparcialidad y eficiencia en costes", text: "Obtén insights objetivos mientras ahorras en recursos internos de auditoría e infraestructura." },
        { title: "Operaciones las 24 horas", text: "Aprovecha las zonas horarias globales para mantener los proyectos en movimiento y acelerar los plazos." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "QbD Group ofrece servicios de auditoría flexibles y escalables. Nuestros auditores certificados y experimentados realizan auditorías internas y a proveedores en tu nombre, con soporte de seguimiento de remediación opcional.",
    processSteps: [
      { title: "Preparación", text: "Consulta inicial para definir objetivos, alcance y criterios de la auditoría. Compartimos una agenda al menos una semana antes de la auditoría." },
      { title: "Ejecución de la auditoría", text: "Evaluación exhaustiva de procedimientos, registros y personal clave. Las observaciones y no conformidades se documentan en detalle." },
      { title: "Informe", text: "Informe completo con visión general de hallazgos, no conformidades y recomendaciones a medida." },
      { title: "Seguimiento", text: "Asistencia en la revisión de planes de acción correctiva para confirmar la eficacia y asegurar que los hallazgos se abordan adecuadamente." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Cuando eliges QbD Group para servicios de auditoría, te beneficias de:",
    partnerBullets: [
      "Experiencia en GxPs — auditores líderes experimentados para auditorías de GMP, MD/IVD, FV y clínicas.",
      "Auditorías globales, base formada en la UE — más de 1.000 auditorías realizadas en regiones clave, con expertos formados en la UE y locales.",
      "Coordinación integral — un coordinador dedicado gestiona la planificación, ejecución e informe — sin sorpresas.",
      "Red de auditoría escalable — expertos internos y freelancers globales listos para picos o necesidades especializadas.",
      "Planificación y presupuesto claros — total visibilidad desde el kickoff hasta el informe, sin costes ocultos.",
      "Comunicación eficaz — contacto alineado y consistente con tu equipo y las plantas auditadas durante todo el proceso.",
      "Herramientas y soporte flexibles — preparados para adaptarse a cambios y auditorías complejas multisite con sistemas expertos.",
    ],
  },
};

const offeringContentZH: Record<string, OfferingSectionTextOverride> = {
  /* ───────── QMS Setup ───────── */
  "qms-setup": {
    whatTitle: "什么是 QMS?",
    whatParagraphs: [
      "质量管理体系 (QMS) 是一套结构化的流程与程序框架,旨在确保 Life Sciences 行业产品的质量、安全与有效性。QbD Group 协助企业落地与其规模、范围与复杂度相匹配的 QMS 解决方案。",
      "QMS 搭建的核心要点包括:<strong class='text-foreground'>法规合规</strong>(ISO 13485:2016、21 CFR 820、欧盟法规 2017/745 与 746、MDSAP)、根据组织具体业务<strong class='text-foreground'>量身定制的解决方案</strong>、覆盖所有必要程序、政策与手册的<strong class='text-foreground'>文件体系</strong>、有效采用与维护 QMS 标准的<strong class='text-foreground'>相关方培训</strong>,以及通过融入提升效率与减少浪费的实践来实现<strong class='text-foreground'>持续改进</strong>。",
    ],
    whyTitle: "为什么 QMS 至关重要?",
    whyIntro: "搭建 QMS 不仅是法规合规的强制性要求,也将为业务带来显著价值:",
    whyBenefits: [
      { title: "认证的强制前提", text: "没有 QMS,就没有产品认证,也无法进入市场。" },
      { title: "运营一致性", text: "标准化的流程能减少错误与低效。" },
      { title: "持续改进", text: "QMS 营造持续优化的环境。" },
      { title: "更优的决策能力", text: "基于证据的流程支持更明智的业务决策。" },
      { title: "客户满意度", text: "更优的产品质量能赢得患者与客户更高的信任。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助?",
      intro: "QbD Group 提供灵活而全面的 QMS 搭建服务,满足您的独特需求:",
      cards: [
        { title: "完整的 QMS 搭建", text: "通过工作坊明确需求、创建所有必要文件,并对相关方进行培训。" },
        { title: "辅导与支持", text: "举办需求入门工作坊,评审并完善组织现有文件。" },
        { title: "初创企业支持", text: "通过入门工作坊,引导团队理解并规划 QMS 的搭建。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活而全面的 QMS 搭建服务。从完整搭建到辅导与初创支持,我们根据您的需求量身打造方案。",
    processSteps: [
      { title: "1. 评估", text: "分析您当前的流程并识别差距。" },
      { title: "2. 工作坊", text: "与相关方协作,理解需求并设计解决方案。" },
      { title: "3. 文件开发", text: "创建符合法规标准的定制化程序、政策与手册。" },
      { title: "4. 培训与落地", text: "为团队赋能,助其有效采用、实施并维护 QMS。" },
    ],
    partnerTitle: "为什么选择 QbD Group?",
    partnerIntro: "选择 QbD Group 搭建您的 QMS,您将获得以下优势:",
    partnerBullets: [
      "经过验证的专业能力 — 在质量保证与法规合规领域积累了数十年的丰富经验。",
      "量身定制的方法 — 方案契合您组织的规模、复杂度与目标。",
      "合规即设计 — 程序自然融入您现有的工作流程。",
      "跨学科知识 — 覆盖 QA、Regulatory Affairs、验证等多领域专长。",
      "可扩展的解决方案 — 服务设计可随您的组织持续成长。",
    ],
  },

  /* ───────── QMS Remediation & Support ───────── */
  "qms-remediation-support": {
    whatTitle: "什么是 QMS 整改与支持?",
    whatParagraphs: [
      "实施 QMS 只是第一步;维护并持续改进它是一个长期过程。QbD Group 的 QMS 整改与支持服务提供专业指导,帮助您保持 QMS 的运行、合规与持续优化。",
      "无论您需要针对审计或检查中发现的差距进行整改,还是需要日常 QMS 活动的支持,我们都能根据您组织的具体需求量身定制服务。核心要点包括<strong class='text-foreground'>运营支持</strong>、<strong class='text-foreground'>差距整改</strong>、<strong class='text-foreground'>持续改进</strong>、<strong class='text-foreground'>文件管理</strong>与<strong class='text-foreground'>合规保障</strong> — 助您获得并维持认证。",
    ],
    whatImageAlt: "QMS 整改与支持 — QbD Group",
    whyTitle: "为什么 QMS 整改与支持至关重要?",
    whyIntro: "维持有效的 QMS 对于法规合规与运营成功至关重要:",
    whyBenefits: [
      { title: "法规更新", text: "法规不断演进,您的 QMS 必须随之调整以保持合规。" },
      { title: "检查准备", text: "完善的文件与流程确保您的 QMS 能够经受审计与检查的检验。" },
      { title: "运营一致性", text: "标准化的流程减少错误,确保运营顺畅。" },
      { title: "持续改进", text: "持续的监控与分析有助于及早发现差距并主动解决。" },
      { title: "风险缓解", text: "及早发现并解决问题,可避免代价高昂的不合规处罚。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助?",
      intro: "QbD Group 提供灵活的 QMS 整改与支持服务,根据您组织的独特需求量身定制:",
      cards: [
        { title: "整改支持", text: "针对检查中发现的差距进行整改,例如 483 表格、警告信或审计发现项。实施纠正与预防措施 (CAPA),确保合规。" },
        { title: "QMS 运营支持", text: "日常 QMS 活动的管理,包括风险评估、文件管理、持续监控、分析与有效性核查。" },
        { title: "量身定制专业能力", text: "由经验丰富的 QA 专业人员为您的团队提供现场或远程支持,从初级到高级,灵活可扩展。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活的 QMS 整改与支持服务,根据您组织的独特需求量身定制,涵盖整改支持、QMS 运营管理以及量身定制专业能力。",
    processSteps: [
      { title: "1. 评估", text: "我们评估您的 QMS,识别差距或改进领域。" },
      { title: "2. 定制化行动计划", text: "我们根据您的需求与法规要求制定量身定制的计划。" },
      { title: "3. 实施", text: "我们的专家协助整改、运营支持与文件管理。" },
      { title: "4. 持续监控", text: "我们通过定期更新与评估,确保您的 QMS 始终保持合规与高效。" },
    ],
    processImageAlt: "QMS 整改流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group?",
    partnerIntro: "选择 QbD Group 提供 QMS 整改与支持,您将获得以下优势:",
    partnerBullets: [
      "丰富的专业能力 — 超过 150 名专家,在中小型与大型组织、多种产品类型及多语言环境中均有丰富经验。",
      "法规知识 — 与主管当局的大量互动经验,确保各个层面的合规。",
      "量身定制的方案 — 灵活的支持设计,精准应对您组织的独特挑战。",
      "经过验证的成功经验 — 在全球客户中保持合规与卓越运营的良好记录。",
      "全球覆盖 — 可根据您的需求提供现场与远程支持。",
    ],
  },

  /* ───────── Outsourced QA ───────── */
  "outsourced-qa": {
    whatTitle: "什么是 QA 外包?",
    whatParagraphs: [
      "QA 外包是指将质量管理体系 (QMS) 的相关活动委托给一支熟悉生命科学法规的专业团队。无论您需要的是整套体系的支持,还是特定质量职能的协助,我们都能提供可扩展且灵活的方案,匹配您组织的需求。",
      "核心要点包括:跨团队、跨工厂与跨供应商的<strong class='text-foreground'>标准化流程</strong>、解决招聘瓶颈的<strong class='text-foreground'>灵活资源模型</strong>、用于填补知识缺口的<strong class='text-foreground'>深厚法规专业能力</strong>、能够<strong class='text-foreground'>无缝扩展 QA 支持</strong>以适应业务变化与波动工作量的能力、<strong class='text-foreground'>主动管理合规风险</strong>以避免昂贵的延误与中断,以及与<strong class='text-foreground'>已验证系统的高效集成</strong>,在成本可控的同时保持质量标准。",
    ],
    whyTitle: "我们如何提供帮助?",
    whyBenefits: [
      { title: "获取专业领域经验", text: "在细分领域具备深厚专业知识,确保精准符合法规要求,降低风险并加速问题解决。" },
      { title: "优化的资源调配", text: "可扩展的远程团队,使企业能够根据项目需求快速增减人力。" },
      { title: "更快的上市速度", text: "在我们处理非核心任务的同时,您可以专注于关键的研发活动,减少瓶颈,加快产品开发。" },
      { icon: Clock, title: "全天候运作", text: "通过跨时区的外包服务,工作可以 24 小时不间断推进,最大化生产力。" },
    ],
    extraSections: [
      {
        title: "我们提供什么:可扩展且量身定制的质量保证专业能力",
        paragraphs: [
          "为您的 QA 活动提供量身定制的支持,包括基础业务支持以及 1–25 名专家组成的可扩展远程团队。",
          "我们的服务目录涵盖:QA、QP、RP、审计师、首席审计师、CAPA、变更、偏差、技术撰稿人与服务经理。",
          "我们在 <strong class='text-foreground'>GMP、GDP、GCP、MD、IVD 与 ATMP</strong> 领域积累了广泛的行业经验,具备区域专业知识与对当地主管机构的深入了解(EU GMP、US GMP、ANVISA、Health Canada)。",
        ],
      },
    ],
    processTitle: "为什么选择 QbD Group?",
    processIntro: "选择 QbD Group 提供 QA 外包服务,您将获得深厚的职能专业能力,以及由资深服务经理提供的务实而高层次的指导。",
    processSteps: [
      { title: "经过验证的可扩展模型", text: "随着您的需求演变,从 QbD Group 的专家资源池中获取专业人才。" },
      { title: "定制化的 QA 解决方案", text: "围绕您的目标量身设计,并由清晰的指标与绩效指引提供保障。" },
      { title: "全球交付选项", text: "具备多语言能力,以及灵活的混合协作模式。" },
      { title: "清晰的预算可视性", text: "从一开始就实现充分的透明与可预测性,确保与财务预期保持一致。" },
    ],
    partnerTitle: "为什么与 QbD Group 合作?",
    partnerIntro: "QbD Group 提供灵活的 QA 外包解决方案,精准匹配您的需求:",
    partnerBullets: [
      "深厚的职能专业能力,由资深服务经理提供务实而高层次的指导。",
      "经过验证的成功经验,可扩展的支持模型可随着您的需求演变,从 QbD Group 的专家资源池中获取专业人才。",
      "围绕您的目标量身设计的定制化 QA 解决方案,并以清晰的指标与绩效指引为支撑。",
      "具备多语言能力的全球交付选项,以及灵活的混合协作模式。",
      "透明且结构化的沟通,包括定期一致的进度报告与升级机制。",
      "从一开始就具备清晰的预算可视性与可预测性,确保与财务预期及运营范围保持一致。",
    ],
  },

  "qualified-person-qp-responsible-person-rp": {
    whatTitle: "什么是 QP/RP 支持?",
    whatParagraphs: [
      "合格人员 (QP) 与负责人 (RP) 是确保药品质量与分销合规所必须的法定角色。这些专业人员负责监督法规要求的执行、管理产品放行,并确保产品始终符合上市许可的要求。",
      "核心要点包括:<strong class='text-foreground'>法规合规</strong>(符合 GMP 或 GDP 标准)、<strong class='text-foreground'>批次放行监督</strong>、<strong class='text-foreground'>文件与记录管理</strong>、<strong class='text-foreground'>灵活的支持模式</strong>(包括全职、临时、项目制或兼职合作),以及<strong class='text-foreground'>专业的领域经验</strong>(在生命科学法规与实践方面具备深厚知识)。",
    ],
    whatImageAlt: "QP 与 RP 服务 — QbD Group",
    whyTitle: "为什么 QP/RP 支持至关重要?",
    whyIntro: "对于制药企业、进口商与分销商而言,配备 QP 或 RP 是强制性要求。这些角色在以下方面至关重要:",
    whyBenefits: [
      { title: "产品放行", text: "符合法规标准是产品进入市场的前提条件。" },
      { title: "审计准备", text: "完善的文件记录与法规遵循能够有效降低检查过程中的风险。" },
      { title: "运营连续性", text: "合格的专业人员填补关键岗位,避免业务中断。" },
      { title: "法规专业能力", text: "具备深厚专业知识的人员确保企业能够持续遵循不断演进的法规。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助?",
      intro: "QbD Group 提供量身定制的 QP 与 RP 服务,满足您的法规与运营需求:",
      cards: [
        { title: "全职支持", text: "配备专属 QP 或 RP,负责日常运营与合规管理。" },
        { title: "临时支持", text: "在审计、人员缺勤或工作量高峰期提供临时覆盖。" },
        { title: "项目制委派", text: "为产品上市或检查准备等特定项目提供专项支持。" },
        { title: "兼职合作", text: "无需全职承诺即可获得灵活的监督与批次审查服务。" },
        { title: "按需专业支持", text: "随时获得 QP 或 RP 资源,应对紧急任务或法规挑战。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供量身定制的 QP 与 RP 服务,从全职支持到按需专家资源,全方位满足您的法规与运营需求。",
    processSteps: [
      { title: "初步咨询", text: "我们详细了解您的具体需求与法规要求。" },
      { title: "候选人甄选", text: "我们根据专业能力与合规知识,推荐合适的 QP/RP 候选人。" },
      { title: "入职衔接", text: "QP 或 RP 顺利融入您的运营,确保平稳过渡。" },
      { title: "持续支持", text: "持续协作,助您达成合规与运营目标。" },
    ],
    partnerTitle: "为什么选择 QbD Group?",
    partnerIntro: "选择 QbD Group 提供 QP/RP 服务,您将获得以下优势:",
    partnerBullets: [
      "即时可用 — 快速填补关键岗位,避免任何延误。",
      "行业领先的专业能力 — 我们的专业人员在 GMP 与 GDP 方面拥有深厚知识。",
      "量身定制的解决方案 — 灵活的合作模式,精准匹配您的具体需求。",
      "经过验证的业绩 — 受到全球生命科学企业的信赖。",
      "可扩展的支持 — 根据您的工作量与法规需求,灵活扩展或缩减支持规模。",
    ],
  },

  /* ───────── Audits & Remediation ───────── */
  "audits-and-remediation": {
    whatTitle: "什么是 QA 审计与整改?",
    whatParagraphs: [
      "QA 审计是任何质量管理体系 (QMS) 的关键组成部分。内部审计与自检确保企业的绩效与合规,而供应商审计则是供应商资格认证与持续监控的必要环节。",
      "QbD Group 根据您的具体需求提供量身定制的审计服务,交付详尽的报告与可执行的洞察。我们审计服务的核心要点包括:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong class='text-foreground'>内部审计:</strong>全面评估,确保您的 QMS 符合法规标准。</li><li><strong class='text-foreground'>供应商审计:</strong>对供应商进行资格认证与监控,确保合规与可靠。</li><li><strong class='text-foreground'>定制化方案:</strong>管理整套审计计划,包括单次审计报告与整体汇总。</li><li><strong class='text-foreground'>详尽报告:</strong>清晰的发现、不符合项,以及纠正与预防措施建议。</li><li><strong class='text-foreground'>后续支持:</strong>可选的整改行动计划复核,确保审计发现得到妥善处理。</li></ul>",
    ],
    whatImageAlt: "QA 审计与整改 — QbD Group",
    whyTitle: "为什么 QA 审计如此重要?",
    whyIntro: "审计不仅是法规要求,更是企业实现成长与改进的契机:",
    whyBenefits: [
      { title: "确保合规", text: "定期审计有助于持续对齐监管机构的期望与不断演进的法规要求。" },
      { title: "优化流程", text: "识别低效环节并推动改进,即便面对分散的团队也能落实行动。" },
      { title: "前瞻识别并管理风险", text: "尽早发现供应商变更等潜在问题,避免业务中断。" },
      { title: "加强现场沟通", text: "深化与本地团队的协作,使执行更加顺畅。" },
      { title: "在压力下展现承诺", text: "即使在运营挑战之下,依然保持卓越的质量与合规水准。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助?",
      intro: "QbD Group 提供灵活且可扩展的审计服务,满足您组织的多样化需求:",
      cards: [
        { title: "专业领域经验", text: "依托细分领域深厚的法规专业知识,确保合规并降低风险。" },
        { title: "灵活的资源配置", text: "快速扩展或精简团队规模,匹配项目需求,避免代价高昂的延误。" },
        { title: "客观且高性价比", text: "获得客观独立的洞察,同时节省内部审计资源与基础设施投入。" },
        { title: "全天候运作", text: "充分利用全球时区优势,推动项目持续推进,加快交付节奏。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "QbD Group 提供灵活且可扩展的审计服务。我们经认证且经验丰富的审计师代表您执行内部审计与供应商审计,并可选择提供后续整改支持。",
    processSteps: [
      { title: "准备", text: "通过初步咨询明确审计目标、范围与标准。我们将在审计前至少一周分享议程。" },
      { title: "审计执行", text: "对流程、记录与关键人员进行全面评估,详细记录观察项与不符合项。" },
      { title: "报告", text: "提供完整报告,涵盖发现总览、不符合项以及量身定制的改进建议。" },
      { title: "后续跟进", text: "协助复核纠正措施计划,确认其有效性,并确保审计发现得到妥善解决。" },
    ],
    processImageAlt: "QA 审计流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group?",
    partnerIntro: "选择 QbD Group 提供审计服务,您将获得以下优势:",
    partnerBullets: [
      "GxP 全领域专业能力 — 资深首席审计师覆盖 GMP、MD/IVD、PV 及临床审计。",
      "全球审计覆盖,以欧盟培养为基础 — 已在关键地区交付 1,000+ 次审计,由欧盟培养与当地专家提供支持。",
      "端到端协调 — 专属协调员负责规划、执行与报告全流程,绝无意外。",
      "可扩展的审计网络 — 内部专家与全球自由审计师随时待命,应对高峰期或专业化需求。",
      "清晰的规划与预算 — 从启动到报告全程透明,无任何隐藏费用。",
      "高效沟通 — 在整个过程中与您的团队及受审现场保持一致而稳定的联系。",
      "灵活的工具与支持 — 凭借专业系统,从容应对变更与多场地复杂审计。",
    ],
  },
};

/**
 * Returns localised offering section data by merging Spanish text overrides
 * onto the English base data. Icons and images are preserved from the base.
 */
export const getLocalizedQAOfferingData = (
  contentKey: string,
  baseData: OfferingSection,
  lang: SiteLang,
): OfferingSection => {
  const override =
    lang === "es"
      ? offeringContentES[contentKey]
      : lang === "zh-cn"
        ? offeringContentZH[contentKey]
        : undefined;
  if (!override) return baseData;

  return {
    ...baseData,
    whatTitle: override.whatTitle,
    whatParagraphs: override.whatParagraphs,
    ...(override.whatImageAlt ? { whatImageAlt: override.whatImageAlt } : {}),
    whyTitle: override.whyTitle,
    whyIntro: override.whyIntro,
    whyBenefits: baseData.whyBenefits.map((b, i) =>
      override.whyBenefits[i]
        ? {
            ...b,
            icon: override.whyBenefits[i].icon ?? b.icon,
            title: override.whyBenefits[i].title,
            text: override.whyBenefits[i].text,
          }
        : b
    ),
    ...(override.helpCards && baseData.helpCards
      ? {
          helpCards: {
            ...baseData.helpCards,
            // The QP/RP authorization caveat is English-only until localized copy is approved
            note: undefined,
            heading: override.helpCards.heading,
            intro: override.helpCards.intro,
            cards: baseData.helpCards.cards.map((c, i) =>
              override.helpCards!.cards[i]
                ? { ...c, title: override.helpCards!.cards[i].title, text: override.helpCards!.cards[i].text }
                : c
            ),
          },
        }
      : {}),
    ...(override.extraSections ? { extraSections: override.extraSections } : {}),
    ...(override.processTitle ? { processTitle: override.processTitle } : {}),
    ...(override.processIntro ? { processIntro: override.processIntro } : {}),
    ...(override.processSteps && baseData.processSteps
      ? {
          processSteps: baseData.processSteps.map((s, i) =>
            override.processSteps![i]
              ? { ...s, title: override.processSteps![i].title, text: override.processSteps![i].text }
              : s
          ),
        }
      : {}),
    ...(override.processImageAlt ? { processImageAlt: override.processImageAlt } : {}),
    partnerTitle: override.partnerTitle,
    partnerIntro: override.partnerIntro,
    partnerBullets: override.partnerBullets,
  };
};

/** UI chrome for QA offering pages */
const qaOfferingUI = {
  en: {
    faqTitle: "Frequently Asked Questions",
    qualityAssurance: "Quality Assurance",
  },
  es: {
    faqTitle: "Preguntas frecuentes",
    qualityAssurance: "Quality Assurance",
  },
  "zh-cn": {
    faqTitle: "常见问题",
    qualityAssurance: "Quality Assurance",
  },
};

export const getQAOfferingUI = (lang: SiteLang) => qaOfferingUI[lang] ?? qaOfferingUI.en;
